/**
 * Users Tab - Phase 8.4 + Phase 9.24
 * User management for admins: view users, invite new users, change roles, remove users
 *
 * Phase 9.24 additions:
 * - Delete user functionality with confirmation
 * - Real email invitations (email is now sent automatically)
 * - Success/warning feedback for email status
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Users, UserPlus, Mail, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { toast } from 'sonner';

interface User {
  uid: string;
  email: string;
  name: string;
  role: string;
  // GET /admin/users returns a tenant member's modules as a list of enabled keys
  // (main.py:723). The per-user ai_connector toggle derives from this list.
  modules?: string[];
}

interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
}

interface InviteResponse {
  success: boolean;
  invite: Invite;
  email_sent: boolean;
  email_error?: string;
}

export function UsersTab() {
  const { user: currentUser, refreshUser } = useAuth();
  const queryClient = useQueryClient();

  // Two-gate AND for the per-user ai_connector toggle (mirrors the backend mint
  // gate): the actor must be a tenant admin AND the tenant must hold mcp_access.
  const isAdmin = currentUser?.is_admin === true;
  const tenantMcpEnabled = currentUser?.tenant?.mcp_access === true;
  const canManageAiConnector = isAdmin && tenantMcpEnabled;
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');

  // Delete confirmation state
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Cancel invite confirmation state
  const [inviteToCancel, setInviteToCancel] = useState<Invite | null>(null);
  const [isCancelInviteDialogOpen, setIsCancelInviteDialogOpen] = useState(false);

  // Fetch users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await api.get('/admin/users');
      return response as { users: User[] };
    },
  });

  // Fetch pending invites
  const { data: invitesData, isLoading: invitesLoading } = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: async () => {
      const response = await api.get('/admin/invites');
      return response as { invites: Invite[] };
    },
  });

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      return api.put(`/admin/users/${userId}`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('Role updated');
    },
    onError: () => {
      toast.error('Failed to update role');
    },
  });

  // Per-user ai_connector grant/revoke mutation. The PUT /admin/users/{id}
  // endpoint merges ONLY the ai_connector key server-side (read-modify-write),
  // so we send just { ai_connector }. Grant is fail-closed behind mcp_access on
  // the backend; the UI also hides the control unless mcp_access is on.
  const updateAiConnectorMutation = useMutation({
    mutationFn: async ({ userId, aiConnector }: { userId: string; aiConnector: boolean }) => {
      return api.put(`/admin/users/${userId}`, { ai_connector: aiConnector });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      // R5 AC3: re-hydrate /auth/me so the actor's own connector gate (and the
      // AI-Connector toggle state) reflect the grant/revoke immediately — no full
      // reload. Best-effort; the user-list invalidate above refreshes the row.
      void refreshUser();
      toast.success(
        variables.aiConnector ? 'AI Connector enabled' : 'AI Connector disabled'
      );
    },
    onError: () => {
      toast.error('Failed to update AI Connector access');
    },
  });

  // Delete user mutation (Phase 9.24)
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api.delete(`/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success('User removed');
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: () => {
      toast.error('Failed to remove user');
    },
  });

  // Create invite mutation (Phase 9.24: now sends email)
  const createInviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      return api.post('/admin/invites', data) as Promise<InviteResponse>;
    },
    onSuccess: (response: InviteResponse) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] });
      setIsInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('user');

      // Show appropriate message based on email status
      if (response.email_sent) {
        toast.success(`Invitation sent to ${response.invite.email}`);
      } else {
        toast.warning(
          `User added but email couldn't be sent. Please notify them manually.`,
          { duration: 5000 }
        );
      }
    },
    onError: () => {
      toast.error('Failed to create invitation');
    },
  });

  // Cancel invite mutation
  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      return api.delete(`/admin/invites/${inviteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] });
      toast.success('Invitation cancelled');
      setIsCancelInviteDialogOpen(false);
      setInviteToCancel(null);
    },
    onError: () => {
      toast.error('Failed to cancel invitation');
    },
  });

  const handleRoleChange = (userId: string, newRole: string) => {
    updateRoleMutation.mutate({ userId, role: newRole });
  };

  const handleAiConnectorChange = (userId: string, enabled: boolean) => {
    updateAiConnectorMutation.mutate({ userId, aiConnector: enabled });
  };

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createInviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.uid);
    }
  };

  const handleCancelInviteClick = (invite: Invite) => {
    setInviteToCancel(invite);
    setIsCancelInviteDialogOpen(true);
  };

  const handleCancelInviteConfirm = () => {
    if (inviteToCancel) {
      cancelInviteMutation.mutate(inviteToCancel.id);
    }
  };

  if (usersLoading || invitesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  const users = usersData?.users || [];
  const invites = invitesData?.invites?.filter(i => i.status === 'pending') || [];

  return (
    <div className="space-y-8">
      {/* Header with Invite Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
        </div>
        <Button onClick={() => setIsInviteDialogOpen(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        <h3 className="text-md font-medium text-gray-700">Active Users</h3>
        <div className="border rounded-lg divide-y">
          {users.map((user) => {
            const isCurrentUser = user.uid === currentUser?.user_id;
            return (
              <div key={user.uid} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Per-user AI Connector toggle — rendered ONLY when the actor
                      is a tenant admin AND the tenant has mcp_access enabled. */}
                  {canManageAiConnector && (
                    <div className="flex items-center gap-2 mr-2">
                      <Checkbox
                        id={`ai-connector-${user.uid}`}
                        checked={user.modules?.includes('ai_connector') ?? false}
                        disabled={updateAiConnectorMutation.isPending}
                        onCheckedChange={(value) =>
                          handleAiConnectorChange(user.uid, value === true)
                        }
                        data-testid={`ai-connector-toggle-${user.uid}`}
                      />
                      <Label
                        htmlFor={`ai-connector-${user.uid}`}
                        className="text-xs text-gray-600 cursor-pointer whitespace-nowrap"
                      >
                        AI Connector
                      </Label>
                    </div>
                  )}
                  <Select
                    value={user.role}
                    onValueChange={(value) => handleRoleChange(user.uid, value)}
                  >
                    <SelectTrigger className="w-32" data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Remove button - only show for other users, not self */}
                  {!isCurrentUser && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(user)}
                      data-testid={`remove-user-${user.uid}`}
                      className="text-gray-400 hover:text-red-600"
                      aria-label={`Remove ${user.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="p-4 text-center text-gray-500">No users found</div>
          )}
        </div>
      </div>

      {/* Pending Invites */}
      <div className="space-y-4">
        <div>
          <h3 className="text-md font-medium text-gray-700">Pending Invites</h3>
          <p className="text-sm text-gray-500 mt-1">
            Users will receive an email invitation to join your organization.
          </p>
        </div>
        <div className="border rounded-lg divide-y">
          {invites.map((invite) => (
            <div key={invite.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-gray-900">{invite.email}</div>
                  <div className="text-sm text-gray-500">Role: {invite.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                  Pending
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCancelInviteClick(invite)}
                  data-testid={`cancel-invite-${invite.id}`}
                  className="text-gray-400 hover:text-red-600"
                  aria-label={`Cancel invitation for ${invite.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {invites.length === 0 && (
            <div className="p-4 text-center text-gray-500">No pending invites</div>
          )}
        </div>
      </div>

      {/* Invite User Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              {/* Phase 9.24: Now sends real email */}
              Add a new user to your organization. An invitation email will be sent to this address with a link to sign up.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin - Full access including user management</SelectItem>
                  <SelectItem value="user">User - Can create and edit projects</SelectItem>
                  <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsInviteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createInviteMutation.isPending}>
                {createInviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog (Phase 9.24) */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {userToDelete?.name || userToDelete?.email} from your organization?
              They will immediately lose access to all projects and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invite Confirmation Dialog */}
      <AlertDialog open={isCancelInviteDialogOpen} onOpenChange={setIsCancelInviteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for {inviteToCancel?.email}?
              They will no longer be able to join using the link they received.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInviteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
